/**
 * Settings (`/settings/$section`, the ⚙ at the end of the tab strip; navigation-ia.md §3.3, §5):
 * You's sections plus global Rules and the Research library, one label per surface. Desktop =
 * section nav + content (artboard `optimal/settings-1440`); phones = the section list, then one
 * section per page with a back link (never a modal). The section bodies are You's modules.
 */
import { Link, Navigate, Outlet, useLocation } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { useEffect, useSyncExternalStore } from 'react'
import { setStatusLine, useStatusLinePref } from '../components/shell/StatusLine.js'
import { SETTINGS_SECTIONS, type SettingsSection } from '../lib/ia.js'
import { RulesPage } from './rules.js'
import { AppearanceSection } from './you/appearance.js'
import { Section } from './you/shared.js'
import './settings.css'

function useWideSafe() {
  const q = '(min-width: 1024px)'
  return useSyncExternalStore(
    (cb) => {
      if (typeof matchMedia === 'undefined') return () => {}
      const m = matchMedia(q)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => typeof matchMedia !== 'undefined' && matchMedia(q).matches,
    () => false,
  )
}

export function SettingsLayout() {
  const wide = useWideSafe()
  const { pathname, hash } = useLocation()
  const id = pathname.split('/')[2]
  const current = SETTINGS_SECTIONS.find((s) => s.id === id)
  useEffect(() => {
    const a = hash.replace(/^#/, '')
    if (a) queueMicrotask(() => document.getElementById(a)?.scrollIntoView?.({ block: 'start' }))
  }, [hash])

  const nav = (
    <nav aria-label="Settings sections" className="you-nav st-navlist">
      <ul className="m-0 grid list-none gap-0.5 p-0">
        {SETTINGS_SECTIONS.map((s) => (
          <li key={s.id}>
            <Link
              to={`/settings/${s.id}` as never}
              className="you-nav__link"
              activeProps={{ 'aria-current': 'page', 'data-active': 'true' }}
            >
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span className="st-navlist__label">{s.label}</span>
                {wide ? null : <span className="st-navlist__blurb">{s.blurb}</span>}
              </span>
              {wide ? null : <ChevronRight size={16} className="text-text-3" aria-hidden="true" />}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
  const title = current?.label ?? (id === 'enroll' ? 'Add a machine' : 'Settings')
  // The page's one title (20/28, 600) with the section's primary action beside it.
  const head = (
    <header className="st-head">
      <h1 className="st-title">{title}</h1>
      {current?.id === 'machines' ? (
        <Link to="/settings/enroll" className="vk-btn vk-btn--secondary vk-btn--sm st-head__act">
          <Plus size={16} aria-hidden="true" />
          <span>Add machine</span>
        </Link>
      ) : null}
    </header>
  )

  if (wide)
    return (
      <div className="app-list flex-1 st">
        <div className="st-grid">
          <div className="st-nav">{nav}</div>
          <div className="st-body">
            {head}
            <Outlet />
          </div>
        </div>
      </div>
    )

  return (
    <div className="app-list flex-1 st st--phone">
      <div className="st-body">
        {current || id === 'enroll' ? (
          <>
            <Link to="/settings" className="st-back">
              <ChevronLeft size={16} aria-hidden="true" />
              <span>Settings</span>
            </Link>
            {head}
            <Outlet />
          </>
        ) : (
          <>
            {head}
            {nav}
          </>
        )}
      </div>
    </div>
  )
}

/** `/settings` on its own: desktop lands on Machines; phones show the section list. */
export function SettingsIndex() {
  const wide = useWideSafe()
  return wide ? <Navigate to={'/settings/machines' as never} replace /> : null
}

export const settingsPath = (id: SettingsSection) => `/settings/${id}` as const

/** Settings › Rules: what every agent is told (project rules live in each project's Settings). */
export function RulesSection() {
  return (
    <Section id="rules" title="Rules">
      <p className="st-lede">
        Short instructions every agent gets. Agents and the weekly retro propose rules; you promote
        the useful ones. A project's own rules are in that project's Settings.
      </p>
      <RulesPage embedded />
    </Section>
  )
}

/** Settings › Appearance and keys: theme, plus the desktop status line (on by default). */
export function AppearanceKeysSection() {
  const on = useStatusLinePref()
  return (
    <>
      <AppearanceSection />
      <Section id="status-line" title="Status line">
        <p className="st-lede">
          The line at the bottom of the window on desktop: where you are, the keys for what you
          selected, machine load and the connection.
        </p>
        <div className="choice st-choice" role="radiogroup" aria-label="Status line">
          <button type="button" role="radio" aria-checked={on} onClick={() => setStatusLine(true)}>
            Show
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!on}
            onClick={() => setStatusLine(false)}
          >
            Hide
          </button>
        </div>
      </Section>
    </>
  )
}
